import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MY_COURSES_CACHE_KEY = 'student-my-courses-cache-v1';
const MY_COURSES_CACHE_TTL_MS = 5 * 60 * 1000;

const readMyCoursesCache = () => {
  if (typeof window === 'undefined') return null;

  try {
    const cachedValue = sessionStorage.getItem(MY_COURSES_CACHE_KEY);
    if (!cachedValue) return null;

    const parsed = JSON.parse(cachedValue);
    if (!parsed?.courses) return null;

    if (Date.now() - (parsed.timestamp || 0) > MY_COURSES_CACHE_TTL_MS) {
      sessionStorage.removeItem(MY_COURSES_CACHE_KEY);
      return null;
    }

    return parsed.courses;
  } catch {
    return null;
  }
};

const writeMyCoursesCache = (courses) => {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem(MY_COURSES_CACHE_KEY, JSON.stringify({
      timestamp: Date.now(),
      courses,
    }));
  } catch {
    // Ignore storage failures and keep the UI responsive.
  }
};

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (!BACKEND_URL) return url;
  return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const getStatusClass = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'completed') return 'bg-green-900 text-green-200 border border-green-700';
  if (normalized === 'upcoming') return 'bg-blue-900 text-blue-200 border border-blue-700';
  return 'bg-yellow-900 text-yellow-200 border border-yellow-700';
};

const MyCoursesPage = () => {
  const navigate = useNavigate();
  const cachedCourses = readMyCoursesCache();
  const [loading, setLoading] = useState(!cachedCourses);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [courses, setCourses] = useState(cachedCourses || []);

  useEffect(() => {
    let isMounted = true;
    const loadCourses = async () => {
      try {
        if (!cachedCourses) {
          setLoading(true);
        }
        setError('');
        const response = await axios.get(`${API}/student/my-courses`);
        if (!isMounted) return;

        const nextCourses = response.data?.courses || [];
        setCourses(nextCourses);
        writeMyCoursesCache(nextCourses);
      } catch (requestError) {
        if (!isMounted) return;
        setError(requestError?.response?.data?.detail || 'Unable to load your enrolled courses.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadCourses();

    return () => {
      isMounted = false;
    };
  }, [cachedCourses]);

  const filteredCourses = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return courses;
    return courses.filter((course) => (
      [
        course.title,
        course.teacher_name,
        course.category,
        course.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query)
    ));
  }, [courses, search]);

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-yellow-400">My Courses</h1>
          <p className="text-gray-300 mt-2">Your private learning dashboard with enrolled courses only.</p>
        </div>

        <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-4 mb-6">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-gray-800 text-white px-4 py-3 rounded-xl border border-gray-600 focus:border-yellow-400 focus:outline-none"
            placeholder="Search by course title, teacher, category, or status"
          />
        </div>

        {loading && <div className="text-gray-400">Loading your enrolled courses...</div>}

        {!loading && error && (
          <div className="bg-red-900/40 border border-red-500 rounded-xl p-4 text-red-200">{error}</div>
        )}

        {!loading && !error && filteredCourses.length === 0 && (
          <div className="bg-gray-900 border border-yellow-400/20 rounded-2xl p-10 text-center">
            <h3 className="text-2xl font-bold text-yellow-400 mb-2">No Enrolled Courses Yet</h3>
            <p className="text-gray-400 mb-6">When your enrollment is approved and active, courses will appear here.</p>
            <button
              onClick={() => navigate('/courses')}
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-3 rounded-lg font-bold"
            >
              Browse Public Catalog
            </button>
          </div>
        )}

        {!loading && !error && filteredCourses.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredCourses.map((course) => {
              const progress = Number(course.progress_percent || 0);
              return (
                <article key={course.id} className="bg-gradient-to-b from-gray-900 to-black border border-yellow-400/20 rounded-2xl overflow-hidden hover:border-yellow-400 transition-all hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(250,204,21,0.12)]">
                  <div className="h-44 bg-gray-800">
                    {course.thumbnail_url ? (
                      <img src={resolveImageUrl(course.thumbnail_url)} alt={course.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-5xl">📘</div>
                    )}
                  </div>

                  <div className="p-6 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getStatusClass(course.status)}`}>{course.status || 'Active'}</span>
                      <span className="text-xs text-gray-400 capitalize">{course.category || 'General'}</span>
                    </div>

                    <h2 className="text-lg font-bold text-white line-clamp-2">{course.title}</h2>
                    <p className="text-sm text-gray-300">Teacher: {course.teacher_name || 'K.R.A Instructor Team'}</p>
                    <p className="text-xs text-gray-400">Enrollment Date: {course.enrollment_date ? new Date(course.enrollment_date).toLocaleDateString() : 'Recently'}</p>
                    <p className="text-xs text-gray-400">Duration: {course.duration || 'Approximately 4 Months'}</p>
                    <p className="text-xs text-gray-400">Next Class: {course.next_class_date || 'Not scheduled yet'}</p>

                    <div>
                      <div className="flex items-center justify-between mb-1 text-xs text-gray-300">
                        <span>Course Progress</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-2 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full" style={{ width: `${progress}%` }}></div>
                      </div>
                    </div>

                    <button
                      onClick={() => navigate(`/student/my-courses/${course.id}`)}
                      className="w-full mt-2 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black py-2.5 px-4 rounded-lg font-semibold"
                    >
                      Continue Learning
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCoursesPage;
